import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Chip,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import AlertModal from '../../components/AlertModal';


const AdvancePayment = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Add Advance Payment Form State
  const [formData, setFormData] = useState({
    associateId: '',
    advanceAmount: '',
    advanceDate: '',
    isInstallment: 'no',
    paymentMode: '',
    remark: ''
  });

  // Modal states
  const [alertConfig, setAlertConfig] = useState({ open: false, title: 'Alert', message: '', type: 'info' });

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };


  // Sample data for Advance Payment Details
  const [advancePaymentData] = useState([
    {
      id: 1,
      associateId: 'ASC001',
      associateName: 'Rajesh Kumar',
      advancePayment: 50000,
      adjustInPayout: 25000,
      isInstallment: 'Yes',
      numberOfInstallment: 2,
      advanceDate: '2024-10-01',
      paymentDetails: 'Cheque - CHQ123456',
      remark: 'Emergency advance',
    },
    {
      id: 2,
      associateId: 'ASC002',
      associateName: 'Priya Sharma',
      advancePayment: 75000,
      adjustInPayout: 75000,
      isInstallment: 'No',
      numberOfInstallment: 1,
      advanceDate: '2024-10-03',
      paymentDetails: 'NEFT - TXN789012',
      remark: 'Festival advance',
    },
    {
      id: 3,
      associateId: 'ASC003',
      associateName: 'Amit Patel',
      advancePayment: 30000,
      adjustInPayout: 10000,
      isInstallment: 'Yes',
      numberOfInstallment: 3,
      advanceDate: '2024-10-05',
      paymentDetails: 'Cash - Receipt #003',
      remark: 'Medical emergency',
    },
    {
      id: 4,
      associateId: 'ASC004',
      associateName: 'Sunita Verma',
      advancePayment: 60000,
      adjustInPayout: 40000,
      isInstallment: 'Yes',
      numberOfInstallment: 2,
      advanceDate: '2024-10-07',
      paymentDetails: 'UPI - UPI456789',
      remark: 'Personal work',
    },
    {
      id: 5,
      associateId: 'ASC005',
      associateName: 'Vikash Singh',
      advancePayment: 45000,
      adjustInPayout: 45000,
      isInstallment: 'No',
      numberOfInstallment: 1,
      advanceDate: '2024-10-08',
      paymentDetails: 'DD - DD567890',
      remark: 'Travel advance',
    },
  ]);

  const paymentModeOptions = ['Cash', 'Cheque', 'DD', 'NEFT', 'RTGS', 'UPI', 'Card'];

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    console.log('Submitting advance payment:', formData);
    showAlert('Advance payment added successfully!', 'success', 'Success');
    // Reset form
    setFormData({
      associateId: '',
      advanceAmount: '',
      advanceDate: '',
      isInstallment: 'no',
      paymentMode: '',
      remark: ''
    });
  };

  const handleSearch = () => {
    console.log('Searching for:', searchTerm);
    // Implement search functionality
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getFilteredData = () => {
    if (!searchTerm) return advancePaymentData;

    return advancePaymentData.filter(item =>
      item.associateId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.associateName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        Advance Payment
      </Typography>

      <Grid container spacing={3}>
        {/* Add Advance Payment Section - Full Width */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
              Add Advance Payment
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Associate ID"
                  value={formData.associateId}
                  onChange={(e) => handleInputChange('associateId', e.target.value)}
                  size="small"
                  placeholder="Enter Associate ID"
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Advance Amount"
                  type="number"
                  value={formData.advanceAmount}
                  onChange={(e) => handleInputChange('advanceAmount', e.target.value)}
                  size="small"
                  placeholder="Enter Amount"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Advance Date"
                  type="date"
                  value={formData.advanceDate}
                  onChange={(e) => handleInputChange('advanceDate', e.target.value)}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Mode</InputLabel>
                  <Select
                    value={formData.paymentMode}
                    label="Payment Mode"
                    onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select Payment Mode</em>
                    </MenuItem>
                    {paymentModeOptions.map((mode) => (
                      <MenuItem key={mode} value={mode}>
                        {mode}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontSize: '0.8rem' }}>Is Installment</FormLabel>
                  <RadioGroup
                    row
                    value={formData.isInstallment}
                    onChange={(e) => handleInputChange('isInstallment', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
                    <FormControlLabel value="no" control={<Radio size="small" />} label="No" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  fullWidth
                  size="small"
                  sx={{ py: 1 }}
                >
                  Submit
                </Button>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remark"
                  multiline
                  rows={1}
                  value={formData.remark}
                  onChange={(e) => handleInputChange('remark', e.target.value)}
                  size="small"
                  placeholder="Enter remarks"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Advance Payment Details Section */}
        {/* <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
              Advance Payment Details
            </Typography>
            
            <Typography variant="body2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
              Click on table below to view and search advance payment records
            </Typography>
          </Paper>
        </Grid> */}
      </Grid>

      {/* Advance Payment Details Table */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">
              Advance Payment Records
            </Typography>
          </Grid>

          {/* Search Box - Right Side and Small */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by Associate ID or Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  size="small"
                  sx={{ py: 0.4, fontSize: '0.75rem', minWidth: '70px' }}
                  fullWidth
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 'bold' }}>
          Total Records: {getFilteredData().length}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Associate ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Associate Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Advance Payment</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Adjust in Payout</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Is Installment</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Number of Installment</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Advance Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Remark</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredData()
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        {row.associateId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {row.associateName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'info.main' }}>
                        {formatCurrency(row.advancePayment)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                        {formatCurrency(row.adjustInPayout)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.isInstallment}
                        size="small"
                        color={row.isInstallment === 'Yes' ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {row.numberOfInstallment}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.advanceDate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {row.paymentDetails}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', maxWidth: '120px' }}>
                        {row.remark}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={getFilteredData().length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          
          sx={{ mt: 2 }}
        />
      </Paper>

      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </Box>
  );
};

export default AdvancePayment;